/**
 * ===========================================================================
 * LA PIEZA DE STREAMING
 * ===========================================================================
 *
 * Hermana de `transcription.ts`, y por la misma razon: el WebSocket vive AQUI
 * y solo aqui. Ni el hook ni el compositor saben que hay un socket -- reciben
 * texto provisional y texto confirmado por callbacks, y les daria igual que
 * por debajo hubiera un socket, un `EventSource` o el propio navegador
 * transcribiendo sin salir a la red.
 *
 * Una garantia que el resto del dictado da por hecha: esta pieza SIEMPRE
 * termina, en `onFailure` o en `onClosed`, y nunca en las dos. Sin eso, un
 * servidor que deja de contestar dejaria el input en cursiva para siempre.
 */
import { API_BASE, wsUrl } from "../../lib/api-routes.ts";
import { currentToken } from "../../lib/auth.ts";

import { CHAT_TEXTS as TEXTS } from "../texts.ts";

/**
 * El formato del audio, y no es negociable.
 *
 * La API realtime de OpenAI solo admite PCM crudo: WebM/Opus no entra por ese
 * socket en ninguna forma. La alternativa era transcodificar en el servidor, y
 * eso mete un decodificador en el camino caliente de cada dictado.
 *
 * Que sea PCM crudo tiene una consecuencia buena que conviene tener presente:
 * no hay contenedor, asi que no hay cabecera que viva solo en el primer trozo
 * ni tramas que no se sostengan solas. Concatenar dos trozos es exactamente el
 * audio de los dos. Con WebM troceado eso NO es cierto, y es justo donde se
 * rompen estas integraciones.
 */
export const LIVE_SAMPLE_RATE = 24000;
export const LIVE_MIME_TYPE = `audio/pcm;rate=${String(LIVE_SAMPLE_RATE)}`;

export interface LiveTranscriptionOptions {
  readonly language: string;
}

export interface LiveTranscriptionHandlers {
  /** El canal esta listo. Hasta aqui, el audio se va guardando. */
  onOpen: () => void;
  /**
   * El texto provisional COMPLETO del dictado hasta ahora.
   *
   * Completo, no el trozo nuevo: el servidor acumula y manda el total en cada
   * mensaje. Quien escucha REEMPLAZA lo que tenia, nunca concatena.
   */
  onInterim: (text: string) => void;
  /** El texto definitivo del dictado entero. Llega una sola vez, al final. */
  onFinal: (text: string) => void;
  /** No se puede seguir en vivo. Quien escucha decide si hay respaldo. */
  onFailure: (message: string) => void;
  /** Cierre sin texto definitivo: no va a llegar nada mas. */
  onClosed: () => void;
}

export interface LiveTranscription {
  /** Manda un trozo de PCM. Antes de que el canal abra, se guarda. */
  push: (chunk: ArrayBuffer) => void;
  /** Pide el cierre ordenado y espera el `final`. */
  stop: () => void;
  /** Corta ya, sin esperar nada ni avisar. */
  close: () => void;
}

export type LiveTranscriptionOpener = (
  options: LiveTranscriptionOptions,
  handlers: LiveTranscriptionHandlers,
) => LiveTranscription;

/**
 * Cuanto se espera el `final` despues de pedir el cierre.
 *
 * Pasado esto se da por terminado con lo que haya. Es preferible consolidar
 * una frase sin revisar que dejar al usuario mirando una cursiva que ya no se
 * va a mover nunca.
 */
const FINAL_TIMEOUT_MS = 4000;

/** Hay navegadores sin WebSocket, y entornos de prueba sin el. */
export const isLiveTranscriptionSupported = (): boolean =>
  typeof (globalThis as { WebSocket?: unknown }).WebSocket === "function";

export const openLiveTranscription: LiveTranscriptionOpener = (
  options,
  handlers,
) => {
  /** Lo capturado antes de que el socket abriera. */
  const pending: ArrayBuffer[] = [];
  let socket: WebSocket | null = null;
  let open = false;
  /** Terminado: ni se manda ni se avisa ya de nada. */
  let done = false;
  let finalTimer: ReturnType<typeof setTimeout> | null = null;

  /** Un solo desenlace: el primero que llegue gana y los demas se ignoran. */
  const settle = (finish: () => void): void => {
    if (done) return;
    done = true;
    if (finalTimer !== null) clearTimeout(finalTimer);
    finalTimer = null;
    finish();
  };

  const closeSocket = (): void => {
    if (socket === null) return;
    const doomed = socket;
    socket = null;
    // Se sueltan los manejadores ANTES de cerrar: si no, el `onclose` que
    // provoca este mismo cierre vuelve a entrar por la puerta de los fallos.
    doomed.onopen = null;
    doomed.onmessage = null;
    doomed.onerror = null;
    doomed.onclose = null;
    try {
      doomed.close();
    } catch {
      // Cerrar un socket que ya se estaba cerrando tira en algun navegador.
    }
  };

  const fail = (message: string): void => {
    settle(() => {
      closeSocket();
      handlers.onFailure(message);
    });
  };

  const closed = (): void => {
    settle(() => {
      closeSocket();
      handlers.onClosed();
    });
  };

  const deliver = (text: string): void => {
    settle(() => {
      closeSocket();
      handlers.onFinal(text);
    });
  };

  const unavailable = (message: string): LiveTranscription => {
    // Se avisa de forma asincrona: quien llama todavia no tiene el objeto que
    // esta funcion devuelve, y llamarle ahora seria entregarselo a medias.
    queueMicrotask(() => {
      fail(message);
    });
    return { push: noop, stop: noop, close: noop };
  };

  if (!isLiveTranscriptionSupported()) {
    return unavailable(TEXTS.voice.liveUnsupported);
  }

  try {
    socket = new WebSocket(liveUrl());
    socket.binaryType = "arraybuffer";
  } catch {
    return unavailable(TEXTS.voice.liveOpenFailed);
  }

  socket.onopen = () => {
    if (done || socket === null) return;
    open = true;
    /**
     * El token va en el PRIMER MENSAJE, no en la query.
     *
     * Un WebSocket del navegador no admite cabeceras propias, así que
     * `Authorization: Bearer` no es una opción. Y un JWT en la query acaba
     * escrito en los logs de acceso de Nginx y de cualquier proxy que haya
     * por medio, donde se queda. En el primer mensaje no queda en ningún log.
     */
    socket.send(
      JSON.stringify({
        type: "start",
        token: currentToken() ?? "",
        language: options.language,
        mimeType: LIVE_MIME_TYPE,
      }),
    );

    for (const chunk of pending) socket.send(chunk);
    pending.length = 0;
    handlers.onOpen();
  };

  socket.onmessage = (event: MessageEvent) => {
    if (done) return;
    const message = parseMessage(event.data);
    if (message === null) return;

    switch (message.type) {
      case "delta": {
        // REEMPLAZA. El servidor acumula por nosotros y manda el total: si
        // esto concatenara, el texto saldria repetido. Ademas un delta puede
        // CORREGIR lo ya dicho cuando el modelo oye mas contexto, y eso solo
        // se puede expresar reemplazando.
        handlers.onInterim(message.text);
        break;
      }
      case "final": {
        // El definitivo del dictado entero, y el ultimo mensaje que manda el
        // servidor. No hay que esperar mas ni detectar el final de nada.
        deliver(message.text);
        break;
      }
      case "error": {
        fail(message.message);
        break;
      }
    }
  };

  socket.onerror = () => {
    fail(TEXTS.voice.liveLost);
  };

  socket.onclose = () => {
    // Un cierre sin `final` previo es un fallo, no un desenlace ordenado. Si
    // el `final` ya llegó, `settle` hace que esto no tenga ningún efecto.
    fail(TEXTS.voice.liveClosed);
  };

  return {
    push: (chunk) => {
      if (done) return;
      if (open && socket !== null) socket.send(chunk);
      else pending.push(chunk);
    },

    stop: () => {
      if (done) return;
      if (!open || socket === null) {
        // Nunca llegó a abrir: no hay `final` que esperar.
        closed();
        return;
      }
      socket.send(JSON.stringify({ type: "stop" }));
      // La red puede no contestar nunca. Este temporizador es lo que hace que
      // el dictado no se quede en cursiva esperando a nadie.
      finalTimer = setTimeout(closed, FINAL_TIMEOUT_MS);
    },

    close: () => {
      settle(closeSocket);
    },
  };
};

const noop = (): void => {
  // El objeto que se devuelve cuando no hay canal: recibe llamadas y no hace
  // nada. Es mas simple que obligar a quien llama a comprobar si lo hay.
};

/**
 * La URL del canal. Sin token: ese viaja en el mensaje `start`.
 */
const liveUrl = (): string => wsUrl(`${API_BASE}/transcriptions/live`);

type LiveMessage =
  | { type: "delta"; text: string }
  | { type: "final"; text: string }
  | { type: "error"; message: string };

/**
 * Lo que llega por el socket es una cadena del otro lado de la red, no un
 * objeto de confianza: se comprueba antes de creérselo. Un mensaje que no
 * encaja se ignora en vez de tumbar el dictado.
 */
const parseMessage = (data: unknown): LiveMessage | null => {
  if (typeof data !== "string") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) return null;
  const message = parsed as {
    type?: unknown;
    text?: unknown;
    message?: unknown;
  };

  if (message.type === "delta" || message.type === "final") {
    return typeof message.text === "string"
      ? { type: message.type, text: message.text }
      : null;
  }

  if (message.type === "error") {
    return {
      type: "error",
      message:
        typeof message.message === "string" && message.message !== ""
          ? message.message
          : "El servidor no pudo transcribir el dictado.",
    };
  }

  return null;
};
